import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, MenuItem, Button, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar
} from "@mui/material";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import {
  fetchExternalConsultant, fetchGetMonths,
  fetchGetYears, fetchGetProjects, createExternalConsultant
} from "../../services/externalconsultantService";
import CenteredCircularProgress from "../../Component/CenteredCircularProgress";
import StickyFooterButtons from "../../Component/StickyFooterButtons";
import { AddCircleOutlineOutlined, PaymentsOutlined } from "@mui/icons-material";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>((props, ref) => (
  <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

type OptionType = { id: number; name: string };

const AddIncentive: React.FC = () => {
  const navigate = useNavigate();

  const [consultantNames, setConsultantNames] = useState<OptionType[]>([]);
  const [months, setMonths] = useState<OptionType[]>([]);
  const [years, setYears] = useState<OptionType[]>([]);
  const [projects, setProjects] = useState<OptionType[]>([]);
  const [selectedConsultant, setSelectedConsultant] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [validationSnackbarOpen, setValidationSnackbarOpen] = useState(false);
  const [addConsultantDialogOpen, setAddConsultantDialogOpen] = useState(false);
  const [newConsultantName, setNewConsultantName] = useState("");

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [consultantRes, monthRes, yearRes, projectRes] = await Promise.all([
          fetchExternalConsultant(),
          fetchGetMonths(),
          fetchGetYears(),
          fetchGetProjects(),
        ]);

        // Consultants
        const uniqueNamesMap: Record<string, boolean> = {};
        const uniqueConsultants: OptionType[] = [];
        for (const item of consultantRes.data || []) {
          const name = item?.consultantName?.trim();
          if (name && !uniqueNamesMap[name]) {
            uniqueNamesMap[name] = true;
            uniqueConsultants.push({ id: item.consultantId, name });
          }
        }
        setConsultantNames(uniqueConsultants);

        // Months
        const fetchedMonths = (monthRes.data || [])
          .filter((m: any) => m.tableName && m.tableID)
          .map((m: any) => ({ id: m.tableID, name: m.tableName }));
        setMonths(fetchedMonths);
        const currentMonth = new Date().toLocaleString("default", { month: "short" });
        const defaultMonth = fetchedMonths.find((m: OptionType) => m.name === currentMonth);
        if (defaultMonth) setSelectedMonth(defaultMonth.name);

        // Years
        const fetchedYears = (yearRes.data || [])
          .filter((y: any) => y.tableValueLong && y.tableID)
          .map((y: any) => ({ id: y.tableID, name: y.tableValueLong }));
        setYears(fetchedYears);
        const currentYear = new Date().getFullYear().toString();
        const defaultYear = fetchedYears.find((y: OptionType) => y.name === currentYear);
        if (defaultYear) setSelectedYear(defaultYear.name);

        // Projects
        const fetchedProjects = (projectRes.data || [])
          .filter((p: any) => p.projectId && p.projectName)
          .map((p: any) => ({ id: p.projectId, name: p.projectName }));
        setProjects(fetchedProjects);

      } catch (err) {
        console.error("Error loading data:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSave = () => {
    if (!selectedConsultant || !selectedMonth || !selectedYear || !amount || !selectedProject) {
      setValidationSnackbarOpen(true);
      return;
    }
    setConfirmDialogOpen(true);
  };

  const confirmSave = async () => {
    setConfirmDialogOpen(false);
    try {
      const existingData = await fetchExternalConsultant();
      const isDuplicate = existingData.data?.some((entry: any) =>
        entry.consultantName?.trim().toLowerCase() === selectedConsultant.trim().toLowerCase() &&
        entry.month?.toLowerCase() === selectedMonth.toLowerCase() &&
        entry.year?.toString() === selectedYear &&
        entry.projectName?.toLowerCase() === selectedProject.toLowerCase()
      );
      if (isDuplicate) {
        setErrorSnackbarOpen(true);
        return;
      }

      const monthObj = months.find((m) => m.name === selectedMonth);
      const yearObj = years.find((y) => y.name === selectedYear);
      const projectObj = projects.find((p) => p.name === selectedProject);
      if (!monthObj || !yearObj || !projectObj) {
        alert("Invalid month, year or project selected.");
        return;
      }

      const newData = {
        createdOn: new Date().toISOString(),
        createdBy: 1,
        modifiedOn: new Date().toISOString(),
        modifiedBy: 1,
        isDeleted: false,
        isActive: true,
        consultantId: 0,
        consultantName: selectedConsultant.trim(),
        monthId: monthObj.id,
        yearId: yearObj.id,
        amount: parseFloat(amount),
        projectID: projectObj.id,
      };

      await createExternalConsultant(newData);
      setSuccessSnackbarOpen(true);
      setTimeout(() => navigate("/Incentivepage"), 2000);
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Failed to save. Check console.");
    }
  };

  const renderSelectField = (
    label: string,
    value: string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    options: OptionType[]
  ) => (
    <TextField select label={label} value={value} onChange={onChange} fullWidth>
      {options.map((opt) => (
        <MenuItem key={opt.id} value={opt.name}>
          {opt.name}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Box sx={{ padding: 3 }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CenteredCircularProgress />
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="flex-start" alignItems="center" px={0} mb={2} gap={2}>
            <PaymentsOutlined fontSize="medium" />
            <Typography variant="h5" fontWeight="bold" mb={0}>
              Add Incentive
            </Typography>
          </Box>

          <Box display="flex" justifyContent="flex-start" px={0}>
            <Box
              width="100%" maxWidth="600px"
              display="flex" flexDirection="column" gap={2}
              sx={{
                padding: { xs: 2, sm: 3 },
                backgroundColor: "#fff",
                borderRadius: 3,
                boxShadow: 3,
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Box flex={1}>
                  {renderSelectField("Consultant Name", selectedConsultant, (e) => setSelectedConsultant(e.target.value), consultantNames)}
                </Box>
                <AddCircleOutlineOutlined
                  color="primary"
                  sx={{ cursor: "pointer" }}
                  onClick={() => setAddConsultantDialogOpen(true)}
                />
              </Box>
              {renderSelectField("Month", selectedMonth, (e) => setSelectedMonth(e.target.value), months)}
              {renderSelectField("Year", selectedYear, (e) => setSelectedYear(e.target.value), years)}
              {renderSelectField("Project", selectedProject, (e) => setSelectedProject(e.target.value), projects)}
              <TextField
                label="Amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
              />
            </Box>
          </Box>

          <StickyFooterButtons
            onSaveClick={handleSave}
            onBackClick={() => navigate("/Incentivepage")}
            isSaveMode={true}
            onEditClick={() => { }}
            isEditMode={false}
          />

          {/* Add Consultant Dialog */}
          <Dialog open={addConsultantDialogOpen} onClose={() => setAddConsultantDialogOpen(false)}>
            <DialogTitle>Add Consultant</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                label="Consultant Name"
                variant="outlined"
                size="small"
                fullWidth
                margin="dense"
                value={newConsultantName}
                onChange={(e) => setNewConsultantName(e.target.value)}
                inputProps={{ maxLength: 50 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddConsultantDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  if (!newConsultantName.trim()) return;

                  // No duplicate check now — allow same consultant name multiple times
                  const newId = Date.now(); // More unique than Math.random
                  setConsultantNames((prev) => [...prev, { id: newId, name: newConsultantName.trim() }]);
                  setSelectedConsultant(newConsultantName.trim());

                  setNewConsultantName("");
                  setAddConsultantDialogOpen(false);
                }}
                autoFocus
              >
                Add
              </Button>
            </DialogActions>
          </Dialog>

          {/* Confirm Save Dialog */}
          <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
            <DialogTitle>Confirm Save</DialogTitle>
            <DialogContent>
              <DialogContentText>Are you sure you want to save this incentive?</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
              <Button onClick={confirmSave} autoFocus>Confirm</Button>
            </DialogActions>
          </Dialog>

          {/* Snackbars */}
          <Snackbar open={successSnackbarOpen} autoHideDuration={2000} onClose={() => setSuccessSnackbarOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
            <Alert onClose={() => setSuccessSnackbarOpen(false)} severity="success" sx={{ width: "100%" }}>
              Incentive Saved Successfully!
            </Alert>
          </Snackbar>

          <Snackbar open={validationSnackbarOpen} autoHideDuration={2000} onClose={() => setValidationSnackbarOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
            <Alert onClose={() => setValidationSnackbarOpen(false)} severity="warning" sx={{ width: "100%" }}>
              Please fill all the fields!
            </Alert>
          </Snackbar>

          <Snackbar open={errorSnackbarOpen} autoHideDuration={2000} onClose={() => setErrorSnackbarOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
            <Alert onClose={() => setErrorSnackbarOpen(false)} severity="error" sx={{ width: "100%" }}>
              Already Exists!
            </Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
};

export default AddIncentive;
